// "How my score is graded" — searchable help hub (Priority 6).
// Left rail = section index; right = markdown body of the selected
// section. URL: /help  → all sections. /help/:sectionId  → deep-linked.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Search, BookOpen, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tiny markdown renderer — good enough for our subset (bold, italic,
// tables, lists, blockquotes, code). Avoids pulling react-markdown into
// the bundle for one page.
const renderMd = (md) => {
  if (!md) return null;
  const lines = md.split('\n');
  const out = [];
  let listBuf = [];
  let tableBuf = [];
  const flushList = () => { if (listBuf.length) { out.push(<ul key={`u${out.length}`} className="list-disc pl-6 my-3 space-y-1 text-sm text-slate-700">{listBuf}</ul>); listBuf = []; } };
  const flushTable = () => {
    if (tableBuf.length < 2) { tableBuf = []; return; }
    const [h, , ...rows] = tableBuf;
    const cells = (line) => line.split('|').map(s => s.trim()).filter((_, i, arr) => i > 0 && i < arr.length - 1);
    const inline = (t) => t.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>');
    out.push(
      <table key={`t${out.length}`} className="text-sm border border-slate-200 rounded my-4 w-full">
        <thead className="bg-slate-50"><tr>{cells(h).map((c, i) => <th key={i} className="text-left px-3 py-2 font-semibold" dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={ri} className="border-t border-slate-100">{cells(r).map((c, i) => <td key={i} className="px-3 py-2" dangerouslySetInnerHTML={{ __html: inline(c) }} />)}</tr>)}</tbody>
      </table>
    );
    tableBuf = [];
  };
  const inlineFmt = (t) => t
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code class="bg-slate-100 px-1 rounded text-xs">$1</code>');

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.startsWith('| ')) { tableBuf.push(line); continue; }
    else flushTable();
    if (line.startsWith('- ')) { listBuf.push(<li key={i} dangerouslySetInnerHTML={{ __html: inlineFmt(line.slice(2)) }} />); continue; }
    else flushList();
    if (line.startsWith('> ')) { out.push(<blockquote key={i} className="border-l-4 border-teal-400 pl-3 my-3 text-sm text-slate-700 italic">{line.slice(2)}</blockquote>); continue; }
    if (/^\d+\. /.test(line)) { out.push(<p key={i} className="text-sm text-slate-700 pl-4" dangerouslySetInnerHTML={{ __html: inlineFmt(line) }} />); continue; }
    if (line.trim() === '') { out.push(<div key={i} className="h-2" />); continue; }
    out.push(<p key={i} className="text-sm text-slate-700 my-2" dangerouslySetInnerHTML={{ __html: inlineFmt(line) }} />);
  }
  flushList();
  flushTable();
  return out;
};

const HelpHubPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { sectionId: paramSection } = useParams();
  const [sections, setSections] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const vertical = user?.vertical || '';
    axios.get(`${API}/help/sections${vertical ? `?vertical=${vertical}` : ''}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setSections(r.data?.sections || []);
        if (paramSection) return axios.get(`${API}/help/sections/${paramSection}`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.data?.sections?.[0]) return axios.get(`${API}/help/sections/${r.data.sections[0].id}`, { headers: { Authorization: `Bearer ${token}` } });
      })
      .then((r) => r && setActive(r.data))
      .catch(() => setSections([]))
      .finally(() => setLoading(false));
  }, [token, paramSection, user?.vertical]);

  useEffect(() => {
    if (query.trim().length < 2) { setSearchResults(null); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/help/search?q=${encodeURIComponent(query)}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setSearchResults(r.data?.matches || []))
        .catch(() => setSearchResults([]));
    }, 200);
    return () => clearTimeout(t);
  }, [query, token]);

  const openSection = (id) => {
    setQuery('');
    setSearchResults(null);
    axios.get(`${API}/help/sections/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setActive(r.data));
    navigate(`/help/${id}`);
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const s of sections) (g[s.category] = g[s.category] || []).push(s);
    return g;
  }, [sections]);

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="help-hub-page">
      <header className="mb-4">
        <h1 className="text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-teal-500" />
          How my score is graded
        </h1>
        <p className="text-sm text-slate-500 mt-1">The full VoiceFabric grading model — searchable, deep-linkable, always up-to-date.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left rail */}
        <aside className="lg:col-span-1 space-y-4">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search sections…"
              className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded text-sm"
              data-testid="help-search-input"
            />
          </div>

          {searchResults ? (
            <div className="border border-slate-200 rounded bg-white p-2 space-y-1 max-h-96 overflow-y-auto" data-testid="help-search-results">
              {searchResults.length === 0 ? (
                <p className="text-xs text-slate-500 py-2">No matches for &quot;{query}&quot;.</p>
              ) : searchResults.map((m) => (
                <button
                  key={m.id}
                  onClick={() => openSection(m.id)}
                  className="w-full text-left px-2 py-1.5 hover:bg-slate-50 rounded"
                  data-testid={`help-search-result-${m.id}`}
                >
                  <p className="text-sm font-semibold text-slate-800">{m.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-2">{m.snippet}</p>
                </button>
              ))}
            </div>
          ) : (
            <nav className="space-y-3">
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">{cat}</p>
                  <ul className="space-y-0.5">
                    {items.map((s) => (
                      <li key={s.id}>
                        <button
                          onClick={() => openSection(s.id)}
                          className={`w-full text-left px-2 py-1 rounded text-sm ${active?.id === s.id ? 'bg-teal-50 text-teal-800 font-semibold' : 'text-slate-700 hover:bg-slate-50'}`}
                          data-testid={`help-nav-${s.id}`}
                        >
                          {s.title}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          )}
        </aside>

        {/* Content */}
        <article className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[400px]" data-testid="help-content-area">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>
          ) : active ? (
            <>
              <h2 className="text-xl font-bold text-slate-900 mb-2" data-testid="help-section-title">{active.title}</h2>
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">{active.category}</p>
              <div>{renderMd(active.body_md)}</div>
            </>
          ) : (
            <p className="text-slate-500">Select a section to read.</p>
          )}
        </article>
      </div>
    </div>
  );
};

export default HelpHubPage;
